import { apiClient, ApiResponse } from './client'
import type { AttendanceStatus } from './attendance'
import type { AttendanceHistoryPage } from './learnerProfile'
import type { LcEnrollment } from './lc'

/** 파생 원생상태 wire 코드 — attendance.ts EnrollmentStatusCode와 동일 집합 (관리자 API용) */
export type LearnerStatusCode = 'WAITING' | 'ENROLLED' | 'ON_LEAVE' | 'WITHDRAWN'

export interface LearnerStatusItem {
  userId: number
  status: LearnerStatusCode
}

export interface LearnerStatusSummary {
  status: LearnerStatusCode
  leaveStart: string | null
  leaveEnd: string | null
  statusChangedAt: string | null
  statusReason: string | null
  today: {
    attendanceStatus: AttendanceStatus | null
    checkInAt: string | null
    checkOutAt: string | null
  }
}

/** 휴원 이력 1건 (P5 축4=A). status='active'(진행중)|'ended'(종료)|'cancelled'(철회). */
export interface LeaveHistoryItem {
  id: number
  leaveStart: string
  leaveEnd: string | null
  reason: string | null
  status: 'active' | 'ended' | 'cancelled'
  createdAt: string
}

export interface ReEnrollPreviousEnrollment {
  lcEnrollmentId: number
  licenseId: number
  previousTimeslotId: number | null
  previousTimeslotName: string | null
  startDate: string | null
  endDate: string | null
  withdrawnAt: string | null
  withdrawalReason: string | null
}

export interface ReEnrollContext {
  userId: number
  previousEnrollments: ReEnrollPreviousEnrollment[]
}

export interface ReEnrollRequest {
  licenseId: number
  timeslotId: number
  startDate?: string
  endDate?: string
  reason: string
}

export interface ReEnrollResponse {
  enrollment: LcEnrollment
  statusEventBatchId: string
}

export const learnerStatusApi = {
  getStatuses: () =>
    apiClient.get<ApiResponse<LearnerStatusItem[]>>('/lc/admin/learners/statuses'),
  getSummary: (userId: number) =>
    apiClient.get<ApiResponse<LearnerStatusSummary>>(`/lc/admin/learners/${userId}/status-summary`),
  startLeave: (userId: number, body: { leaveStart?: string; leaveEnd?: string; reason?: string }) =>
    apiClient.post<ApiResponse<{ updated: number }>>(`/lc/admin/learners/${userId}/leave`, body),
  returnFromLeave: (userId: number) =>
    apiClient.post<ApiResponse<{ updated: number }>>(`/lc/admin/learners/${userId}/return`, {}),
  withdraw: (userId: number, body: { reason?: string }) =>
    apiClient.post<ApiResponse<{ updated: number }>>(`/lc/admin/learners/${userId}/withdraw`, body),
  getReEnrollContext: (userId: number) =>
    apiClient.get<ApiResponse<ReEnrollContext>>(`/lc/admin/learners/${userId}/re-enroll-context`),
  reEnroll: (userId: number, body: ReEnrollRequest) =>
    apiClient.post<ApiResponse<ReEnrollResponse>>(`/lc/admin/learners/${userId}/re-enroll`, body),
  getAttendanceHistory: (userId: number, params: { cursor?: string | null; limit?: number }) =>
    apiClient.get<ApiResponse<AttendanceHistoryPage>>(
      `/lc/admin/learners/${userId}/attendance-history`,
      { params: { cursor: params.cursor ?? undefined, limit: params.limit ?? 30 } },
    ),
  // P5 축4=A: 휴원 이력 목록/수정/개별취소
  getLeaveHistory: (userId: number) =>
    apiClient.get<ApiResponse<LeaveHistoryItem[]>>(`/lc/admin/learners/${userId}/leave-history`),
  updateLeaveHistory: (
    userId: number,
    historyId: number,
    body: { leaveStart?: string; leaveEnd?: string; reason?: string },
  ) =>
    apiClient.put<ApiResponse<LeaveHistoryItem>>(`/lc/admin/learners/${userId}/leave-history/${historyId}`, body),
  cancelLeaveHistory: (userId: number, historyId: number) =>
    apiClient.delete<ApiResponse<{ updated: number }>>(`/lc/admin/learners/${userId}/leave-history/${historyId}`),
}
